import { OPERATORS } from '../../constants';
import { BaseOperatorStrategy } from './base.operator-strategy';
import type {
  SelectionBlockerArgs,
  SelectionBlockerResult,
  UnitCostCalculationArgs,
  ModeSelectionContext,
} from '../operator-strategy';

/**
 * Synspective strategy — matches the ICEYE pattern for mode-based latency pricing.
 *
 * Key rule:
 *   selectedModes[0].pricing[latencyKey] is the source of truth for per-tier cost.
 * The mode selection determines which latency tiers are valid and what their price is.
 */
export class SynspectiveOperatorStrategy extends BaseOperatorStrategy {
  readonly operatorKey = OPERATORS.SYNSPECTIVE;

  override applyResolutionConstraints(
    sensorRow: any,
    productMode: any,
  ): boolean {
    if (!Array.isArray(sensorRow?.resolution)) return false;
    const targetDistance = productMode?.distance;
    sensorRow.selectedResolutionData = null;
    for (const item of sensorRow.resolution) {
      if (item.value === targetDistance) {
        item.disable = false;
        sensorRow.selectedResolution = item;
        sensorRow.selectedResolutionValue = item.value;
      } else {
        item.disable = true;
      }
    }

    // Sync ONA selection from the mode's minONA/maxONA fields.
    if (
      Array.isArray(sensorRow.ONA) &&
      productMode?.minONA != null &&
      productMode?.maxONA != null
    ) {
      const match = sensorRow.ONA.find(
        (o: any) =>
          o.minONA === productMode.minONA && o.maxONA === productMode.maxONA,
      );
      if (match) {
        sensorRow.selectedONA = match.key;
        sensorRow.selectedItem = match;
        sensorRow.selectedONAOption = match;
      }
    }

    return true;
  }

  protected computeBaseCost(sensorRow: any, latencyIndx: number): number | null {
    if (!sensorRow || !Array.isArray(sensorRow.latency)) return null;
    this.syncLatencyCosts(sensorRow);
    return Number(sensorRow.latency[latencyIndx]?.cost ?? 0);
  }

  override syncLatencyCosts(sensorRow: any): void {
    if (!sensorRow || !Array.isArray(sensorRow.latency)) return;

    const pricingArray = Array.isArray(sensorRow.selectedModes)
      ? sensorRow.selectedModes[0]?.pricing
      : sensorRow.selectedModes?.pricing;
    if (!pricingArray) return;

    if (!sensorRow._allLatency) {
      sensorRow._allLatency = [...sensorRow.latency];
    }

    sensorRow.latency = sensorRow._allLatency.filter(
      (latencyItem: any) =>
        Object.prototype.hasOwnProperty.call(pricingArray, latencyItem.key) &&
        pricingArray[latencyItem.key] != null,
    );

    for (const latencyItem of sensorRow.latency) {
      const newCost = Number(pricingArray[latencyItem.key]);
      latencyItem.cost = newCost;
      latencyItem.unitcost = newCost;
    }
  }

  resolveActiveLatency(sensorRow: any): any | null {
    if (!Array.isArray(sensorRow?.latency) || sensorRow.latency.length === 0) {
      return null;
    }

    const current = sensorRow.latency.find((item: any) => item.selected === true);
    if (current && (current.cost || 0) > 0) return current;

    return sensorRow.latency.find((item: any) => (item.cost || 0) > 0) ?? null;
  }

  getSelectionBlocker(args: SelectionBlockerArgs): SelectionBlockerResult | null {
    if (args?.taskingContext?.isRecurrent) {
      return this.createUnsupportedRecurrencyBlocker(
        args?.sensorRow,
        'SYNSPECTIVE_RECURRENCY_UNSUPPORTED',
      );
    }

    return this.getMaxEndDaysBlocker(args);
  }

  private getMaxEndDaysBlocker(args: SelectionBlockerArgs): SelectionBlockerResult | null {
    if (!args.toiEndUtc) return null;

    const sensorRow = args?.sensorRow;
    const maxEndDays: number = sensorRow?.maxEndDaysFromNow ?? 14;
    const daysFromNowToEnd = Math.ceil(
      (args.toiEndUtc.getTime() - this.resolveSubmissionDate().getTime()) / 86_400_000,
    );

    if (daysFromNowToEnd <= maxEndDays) return null;

    return {
      code: 'SYNSPECTIVE_TOI_MAX_END_DAYS',
      message: `${sensorRow?.displayName} ${sensorRow?.sensor} requires the time of interest (TOI) to be ${maxEndDays} days or less from the current date. Please change your TOI or choose an alternative provider.`,
    };
  }

  selectDefaultMode(
    collectionModes: any[] = [],
    context: ModeSelectionContext,
  ): any | null {
    if (!collectionModes.length) return null;

    const isStripMode = (mode: any): boolean =>
      (mode?.captureMode ?? mode?.key ?? '')
        .toString()
        .toLowerCase()
        .includes('stripmap');

    if (context?.targetType === 'AOI') {
      return collectionModes.find((mode) => isStripMode(mode)) ?? collectionModes[0];
    }

    return collectionModes.find((mode) => !isStripMode(mode)) ?? collectionModes[0];
  }


}
